import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import AdminApp from './admin/AdminApp';
import SiteHeader from './components/SiteHeader';
import SiteFooter from './components/SiteFooter';
import AccountModals from './components/AccountModals';
import ProfilePage from './components/ProfilePage';
import { AccountProvider, useAccount } from './account/context';
import { trackPageview } from './lib/track';
import './styles/tailwind.css';

const el = document.getElementById('root');
if (!el) throw new Error('#root not found');

// Hash routing: #admin → admin panel, #account → profile page, else the scan app.
function Root(): JSX.Element {
  const [hash, setHash] = useState(window.location.hash);
  useEffect(() => {
    const onHash = (): void => setHash(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Record a pageview whenever the public app is shown (deduped by pathname, so
  // one beacon per visit). The operator's own #admin view is excluded so it
  // doesn't pollute the visitor stats.
  useEffect(() => {
    if (hash !== '#admin') trackPageview();
  }, [hash]);

  if (hash === '#admin') return <AdminApp />;

  return (
    <AccountProvider>
      <Public />
      <AccountModals />
    </AccountProvider>
  );
}

/**
 * Site chrome, or the dashboard shell. **Signing in is what decides, not the
 * route.**
 *
 * D-23 says this app renders the pagecoder.ai header and footer so it looks
 * integrated, and that holds for everyone who is not signed in: the scan page,
 * the explainer, the pricing and the FAQ are the marketing surface, the SEO
 * surface and where the LinkedIn post lands.
 *
 * A signed-in client is not a visitor. For them the top nav is five links to
 * pages they are not using, wrapped around a tool they are working in.
 *
 * The first version switched on the route instead, so a signed-in client got
 * the marketing layout on the scan page and the dashboard only once they
 * reached their account. The layout changed underneath them halfway through
 * using the product, which is worse than either layout on its own.
 *
 * While `/me` is still resolving, `me` is null and the site chrome renders.
 * That is the right thing to show somebody who may turn out to be signed out,
 * and it settles within one request.
 *
 * `useAccount` has to be called inside the provider, which is why this is a
 * component rather than a branch up in `Root`.
 */
function Public(): JSX.Element {
  const { account: me } = useAccount();

  if (me) return <ProfilePage />;

  return (
    <div className="min-h-screen flex flex-col bg-warm-dark">
      <SiteHeader />
      <div className="flex-1">{window.location.hash.startsWith('#account') ? <ProfilePage /> : <App />}</div>
      <SiteFooter />
    </div>
  );
}

createRoot(el).render(<StrictMode><Root /></StrictMode>);
